import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { Helmet } from "react-helmet";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  list: {
    paddingLeft: theme.spacing(3),
  },
  contact: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  highlight: {
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
}));

const TermsOfService = () => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Termos de Serviço - ChatFox</title>
      </Helmet>
      <Container maxWidth="md">
        <Paper className={classes.paper}>
          <Typography variant="h3" className={classes.title} align="center">
            Termos de Serviço
          </Typography>

          <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </Typography>

          {/* 1. Identificação do Serviço */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              1. Identificação do Serviço
            </Typography>
            <Typography variant="body1" paragraph>
              Estes Termos de Serviço regulam o uso do aplicativo <strong>ChatFox</strong>, 
              uma plataforma de atendimento multicanal e automação de mensagens, operada por 
              <strong> ChatFox Tecnologia</strong>, disponível por meio das APIs oficiais da Meta 
              (WhatsApp Business API, Instagram Graph API e Facebook Graph API).
            </Typography>
            <div className={classes.contact}>
              <Typography variant="body2">
                <strong>Nome do Aplicativo:</strong> ChatFox
              </Typography>
              <Typography variant="body2">
                <strong>Responsável:</strong> ChatFox Tecnologia
              </Typography>
              <Typography variant="body2">
                <strong>E-mail de Contato:</strong> suporte@chatfox.com.br
              </Typography>
              <Typography variant="body2">
                <strong>País:</strong> Brasil
              </Typography>
            </div>
          </div>

          {/* 2. Aceitação dos Termos */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              2. Aceitação dos Termos
            </Typography>
            <Typography variant="body1" paragraph>
              Ao utilizar o aplicativo ChatFox, o usuário declara que leu, compreendeu e 
              concorda integralmente com estes Termos de Serviço e com a Política de Privacidade.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Caso não concorde com estes termos, não utilize o serviço.</strong>
            </Typography>
          </div>

          {/* 3. Descrição do Serviço */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              3. Descrição do Serviço
            </Typography>
            <Typography variant="body1" paragraph>
              O ChatFox é uma plataforma que oferece:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Atendimento ao cliente via WhatsApp Business, Instagram Direct e Facebook Messenger</Typography></li>
              <li><Typography variant="body1">Automação de mensagens através de chatbots e fluxos de conversa</Typography></li>
              <li><Typography variant="body1">Gerenciamento de filas de atendimento e distribuição de tickets</Typography></li>
              <li><Typography variant="body1">Envio de templates de mensagens aprovados pela Meta</Typography></li>
              <li><Typography variant="body1">Integração com múltiplos canais de comunicação</Typography></li>
              <li><Typography variant="body1">Relatórios e métricas de atendimento</Typography></li>
              <li><Typography variant="body1">Gestão de contatos e histórico de conversas</Typography></li>
            </ul>
            <div className={classes.highlight}>
              <Typography variant="body2">
                <strong>Importante:</strong> O serviço depende da disponibilidade das APIs da Meta. 
                Não garantimos disponibilidade ininterrupta devido a fatores externos fora do nosso controle.
              </Typography>
            </div>
          </div>

          {/* 4. Regras de Uso */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              4. Regras de Uso
            </Typography>
            
            <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
              4.1. Uso Permitido
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Uso para atendimento ao cliente e suporte</Typography></li>
              <li><Typography variant="body1">Envio de mensagens iniciadas pelo usuário final</Typography></li>
              <li><Typography variant="body1">Envio de templates de mensagens aprovados pela Meta</Typography></li>
              <li><Typography variant="body1">Automação de respostas para melhorar o atendimento</Typography></li>
              <li><Typography variant="body1">Gerenciamento de conversas e histórico de atendimento</Typography></li>
            </ul>

            <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
              4.2. Uso Proibido
            </Typography>
            <div className={classes.highlight}>
              <Typography variant="body1" paragraph>
                <strong>É estritamente proibido:</strong>
              </Typography>
              <ul className={classes.list}>
                <li><Typography variant="body1">Envio de spam ou mensagens não solicitadas</Typography></li>
                <li><Typography variant="body1">Compartilhamento de conteúdo ilegal, ofensivo, discriminatório ou fraudulento</Typography></li>
                <li><Typography variant="body1">Violação das Políticas de Uso da Meta (WhatsApp, Instagram, Facebook)</Typography></li>
                <li><Typography variant="body1">Coleta não autorizada de dados de usuários</Typography></li>
                <li><Typography variant="body1">Uso para fins de phishing, fraude ou atividades maliciosas</Typography></li>
                <li><Typography variant="body1">Tentativa de burlar limitações técnicas ou de segurança</Typography></li>
                <li><Typography variant="body1">Revenda ou sublicenciamento do serviço sem autorização</Typography></li>
              </ul>
            </div>
          </div>

          {/* 5. Obrigações do Usuário */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              5. Obrigações do Usuário
            </Typography>
            <Typography variant="body1" paragraph>
              O usuário é responsável por:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Obter autorização prévia dos destinatários para envio de mensagens</Typography></li>
              <li><Typography variant="body1">Cumprir a Lei Geral de Proteção de Dados (LGPD) e GDPR quando aplicável</Typography></li>
              <li><Typography variant="body1">Respeitar as Políticas de Uso da Meta (WhatsApp Business, Instagram, Facebook)</Typography></li>
              <li><Typography variant="body1">Manter a segurança de suas credenciais de acesso</Typography></li>
              <li><Typography variant="body1">Garantir que o conteúdo enviado não viola direitos de terceiros</Typography></li>
              <li><Typography variant="body1">Responder por qualquer uso indevido de sua conta</Typography></li>
              <li><Typography variant="body1">Notificar imediatamente sobre qualquer uso não autorizado</Typography></li>
            </ul>
          </div>

          {/* 6. Obrigações do Aplicativo */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              6. Obrigações do Aplicativo
            </Typography>
            <Typography variant="body1" paragraph>
              O ChatFox se compromete a:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Disponibilizar o serviço conforme descrito, dentro das possibilidades técnicas</Typography></li>
              <li><Typography variant="body1">Proteger os dados dos usuários conforme a Política de Privacidade</Typography></li>
              <li><Typography variant="body1">Manter conformidade com as APIs oficiais da Meta</Typography></li>
              <li><Typography variant="body1">Implementar medidas de segurança para proteção da plataforma</Typography></li>
              <li><Typography variant="body1">Fornecer suporte técnico através dos canais oficiais</Typography></li>
              <li><Typography variant="body1">Notificar sobre alterações significativas nos termos ou no serviço</Typography></li>
            </ul>
          </div>

          {/* 7. Limitação de Responsabilidade */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              7. Limitação de Responsabilidade
            </Typography>
            <div className={classes.highlight}>
              <Typography variant="body1" paragraph>
                <strong>O serviço é fornecido "como está" (as is).</strong>
              </Typography>
              <Typography variant="body1" paragraph>
                O ChatFox não se responsabiliza por:
              </Typography>
              <ul className={classes.list}>
                <li><Typography variant="body1">Bloqueios, suspensões ou limitações impostas pela Meta</Typography></li>
                <li><Typography variant="body1">Interrupções ou instabilidades nas APIs da Meta</Typography></li>
                <li><Typography variant="body1">Uso indevido da plataforma pelo usuário ou terceiros</Typography></li>
                <li><Typography variant="body1">Perda de dados causada por falhas técnicas ou ações de terceiros</Typography></li>
                <li><Typography variant="body1">Danos indiretos, lucros cessantes ou prejuízos comerciais</Typography></li>
                <li><Typography variant="body1">Conteúdo enviado pelos usuários através da plataforma</Typography></li>
                <li><Typography variant="body1">Violações de políticas da Meta cometidas pelo usuário</Typography></li>
              </ul>
            </div>
          </div>

          {/* 8. Suspensão e Encerramento */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              8. Suspensão e Encerramento
            </Typography>
            <Typography variant="body1" paragraph>
              O ChatFox reserva-se o direito de suspender ou encerrar o acesso ao serviço nas seguintes situações:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Violação destes Termos de Serviço</Typography></li>
              <li><Typography variant="body1">Violação das Políticas da Meta</Typography></li>
              <li><Typography variant="body1">Uso fraudulento ou malicioso da plataforma</Typography></li>
              <li><Typography variant="body1">Exigência legal ou determinação judicial</Typography></li>
              <li><Typography variant="body1">Determinação ou bloqueio imposto pela Meta</Typography></li>
              <li><Typography variant="body1">Inadimplência no pagamento (quando aplicável)</Typography></li>
            </ul>
            <Typography variant="body1" paragraph>
              A suspensão ou encerramento pode ocorrer sem aviso prévio em casos graves.
            </Typography>
          </div>

          {/* 9. Propriedade Intelectual */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              9. Propriedade Intelectual
            </Typography>
            <Typography variant="body1" paragraph>
              Todos os direitos de propriedade intelectual sobre o ChatFox, incluindo software, 
              design, marca e conteúdo, pertencem ao ChatFox Tecnologia.
            </Typography>
            <Typography variant="body1" paragraph>
              As marcas WhatsApp, Instagram, Facebook e Meta pertencem à Meta Platforms, Inc.
            </Typography>
            <Typography variant="body1" paragraph>
              O usuário não adquire nenhum direito de propriedade sobre o software e não pode:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Copiar, modificar ou criar obras derivadas</Typography></li>
              <li><Typography variant="body1">Fazer engenharia reversa do software</Typography></li>
              <li><Typography variant="body1">Revender ou sublicenciar o serviço</Typography></li>
              <li><Typography variant="body1">Remover avisos de direitos autorais ou marcas</Typography></li>
            </ul>
          </div>

          {/* 10. Alterações nos Termos */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              10. Alterações nos Termos de Serviço
            </Typography>
            <Typography variant="body1" paragraph>
              O ChatFox reserva-se o direito de modificar estes Termos de Serviço a qualquer momento.
            </Typography>
            <Typography variant="body1" paragraph>
              Quando houver alterações significativas, os usuários serão notificados através de:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">E-mail cadastrado na plataforma</Typography></li>
              <li><Typography variant="body1">Aviso na plataforma ao fazer login</Typography></li>
              <li><Typography variant="body1">Atualização da data no topo deste documento</Typography></li>
            </ul>
            <Typography variant="body1" paragraph>
              O uso continuado do serviço após as alterações constitui aceitação dos novos termos.
            </Typography>
          </div>

          {/* 11. Lei Aplicável e Foro */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              11. Lei Aplicável e Foro
            </Typography>
            <Typography variant="body1" paragraph>
              Estes Termos de Serviço são regidos pelas leis da República Federativa do Brasil.
            </Typography>
            <Typography variant="body1" paragraph>
              Fica eleito o foro da comarca de domicílio do usuário para dirimir quaisquer 
              controvérsias decorrentes destes termos, renunciando-se a qualquer outro, por mais privilegiado que seja.
            </Typography>
          </div>

          {/* 12. Disposições Gerais */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              12. Disposições Gerais
            </Typography>
            <Typography variant="body1" paragraph>
              Se qualquer disposição destes Termos for considerada inválida ou inexequível, 
              as demais disposições permanecerão em pleno vigor e efeito.
            </Typography>
            <Typography variant="body1" paragraph>
              A tolerância quanto ao descumprimento de qualquer obrigação não constitui renúncia 
              ou novação, podendo ser exigida a qualquer tempo.
            </Typography>
          </div>

          {/* Contato */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Dúvidas ou Suporte
            </Typography>
            <Typography variant="body1" paragraph>
              Para dúvidas sobre estes Termos de Serviço ou suporte técnico, entre em contato:
            </Typography>
            <div className={classes.contact}>
              <Typography variant="body2">
                <strong>E-mail:</strong> suporte@chatfox.com.br
              </Typography>
              <Typography variant="body2">
                <strong>Plataforma:</strong> ChatFox - Atendimento Multicanal
              </Typography>
            </div>
          </div>

          <div className={classes.highlight} style={{ marginTop: 32 }}>
            <Typography variant="body2" align="center">
              Ao utilizar o ChatFox, você concorda com estes Termos de Serviço e com a Política de Privacidade.
            </Typography>
          </div>
        </Paper>
      </Container>
    </div>
  );
};

export default TermsOfService;
